import { defineStore } from "pinia";
import type { ScannedCode } from "@/constants/barcode";

/**
 * Drives the app-wide QR scanner overlay.
 *
 * The scanner is deliberately NOT a page: staff scan a pet tag or a kennel door
 * while already in the middle of something (a visit, the boarding board, POS),
 * and being navigated away would lose that context. Any surface can call
 * `open()`; the overlay mounted once in `App.vue` handles the camera and shows
 * the result modal on top of whatever is currently open.
 */
const useQrScannerStore = defineStore("qr-scanner", () => {
  /** Camera dialog visibility. */
  const scannerOpen = ref(false);
  /** The resolved code whose panel is being shown, if any. */
  const result = ref<ScannedCode | null>(null);

  const hasResult = computed(() => Boolean(result.value));

  /** Open the camera. Clears any previous result so the overlay starts clean. */
  function open() {
    result.value = null;
    scannerOpen.value = true;
  }

  /** Show a resolved code's panel (used after a decode or a manual lookup). */
  function show(code: ScannedCode) {
    result.value = code;
    scannerOpen.value = false;
  }

  /** Dismiss the result panel, leaving the underlying page untouched. */
  function closeResult() {
    result.value = null;
  }

  function closeAll() {
    scannerOpen.value = false;
    result.value = null;
  }

  return { scannerOpen, result, hasResult, open, show, closeResult, closeAll };
});

export default useQrScannerStore;
